import { useState, useId } from 'react';
import { Button } from './ui/button';
import { Upload, X, Image as ImageIcon, Video, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToR2 } from '../lib/r2Upload';

// Media upload helper - supports both images and videos
const uploadMedia = async (file, folder = 'uploads') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  const response = await fetch('https://esayt-upload-worker.najaflisaid1.workers.dev/upload', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Fayl yüklənə bilmədi');
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Fayl yüklənə bilmədi');
  }

  return result.publicUrl;
};

export default function MultiMediaUpload({ 
  onUploadComplete, 
  currentMedia = [], 
  maxFiles = 10,
  allowVideo = true,
  folder = "products" 
}) {
  const [uploading, setUploading] = useState(false);
  const [mediaList, setMediaList] = useState(currentMedia);
  const inputId = useId();

  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max files limit
    if (mediaList.length + files.length > maxFiles) {
      toast.error(`Maksimum ${maxFiles} fayl yükləyə bilərsiniz`);
      return;
    }

    // Validate files
    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideoFile = file.type.startsWith('video/');
      
      if (!isImage && !isVideoFile) {
        toast.error('Yalnız şəkil və video faylları yükləyə bilərsiniz');
        return;
      }

      if (!allowVideo && isVideoFile) {
        toast.error('Video yükləmə deaktivdir');
        return;
      }

      // Size limits: 5MB for images, 100MB for videos
      const maxSize = isVideoFile ? 100 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${isVideoFile ? 'Video 100MB' : 'Şəkil 5MB'}-dan kiçik olmalıdır`);
        return;
      }
    }

    setUploading(true);
    const newMedia = [...mediaList];

    try {
      for (const file of files) {
        console.log('Uploading:', file.name);
        const mediaUrl = await uploadMedia(file, folder);
        console.log('Uploaded:', mediaUrl);
        newMedia.push({
          url: mediaUrl,
          type: file.type.startsWith('video/') ? 'video' : 'image'
        });
      }

      setMediaList(newMedia);
      onUploadComplete(newMedia);
      toast.success(`${files.length} fayl yükləndi`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Fayl yüklənə bilmədi');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = (index) => {
    const newMedia = mediaList.filter((_, i) => i !== index);
    setMediaList(newMedia);
    onUploadComplete(newMedia);
  };

  return (
    <div className="space-y-4">
      {/* Media Grid */}
      {mediaList.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {mediaList.map((media, index) => (
            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
              {media.type === 'video' || isVideo(media.url) ? (
                <video
                  src={media.url || media}
                  className="w-full h-full object-cover"
                  muted
                />
              ) : (
                <img
                  src={media.url || media}
                  alt={`Media ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute top-1 right-1 h-6 w-6 p-0"
                onClick={() => handleRemove(index)}
                disabled={uploading}
              >
                <X className="w-3 h-3" />
              </Button>
              {media.type === 'video' && (
                <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                  <Video className="w-3 h-3 inline mr-1" />
                  Video
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {mediaList.length < maxFiles && (
        <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-primary animate-spin mb-2" />
              <p className="text-sm text-muted-foreground">Yüklənir...</p>
            </div>
          ) : (
            <>
              <div className="flex justify-center gap-2 mb-3">
                <ImageIcon className="w-8 h-8 text-muted-foreground" />
                {allowVideo && <Video className="w-8 h-8 text-muted-foreground" />}
              </div>
              <p className="text-sm text-muted-foreground mb-1">
                Şəkil {allowVideo && 'və video'} yükləyin
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                {allowVideo ? 'Şəkil max 5MB, Video max 100MB' : 'Maksimum 5MB'}
              </p>
              <input
                type="file"
                id={inputId}
                accept={allowVideo ? "image/*,video/*" : "image/*"}
                onChange={handleFileSelect}
                className="hidden"
                multiple
                disabled={uploading}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById(inputId).click()}
                disabled={uploading}
              >
                <Plus className="w-4 h-4 mr-2" />
                Fayl Seç ({mediaList.length}/{maxFiles})
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
